"use client";

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { Sheet } from "@/components/ui/Sheet";
import { DifficultyScale } from "@/components/event/DifficultyScale";
import { difficultyGrade, difficultyLabel } from "@/components/event/labels";

/**
 * How hard a trail is, as a strip going from green to red between Fácil and
 * Difícil and a ▼ over it at the trail's grade (montanhas-web#22; the squares
 * filled up to the grade before it, and the other ways tried are
 * proposals/difficulty-bar, outside git). The grade's name is only its tooltip
 * and what a screen reader says (the user's call, 2026-10-29). Nothing for a
 * difficulty that is not one of the six.
 *
 * It opens the FAQ tagged dificuldade, and reads as a link where it does.
 * `compact` leaves out Fácil and Difícil and the FAQ, for a bar inside
 * something that is itself pressed, such as the trails' summary; `words` puts
 * the two words back there, as plain text, where there is room for them.
 * `size` is the width of one of the six steps, as it was of a square.
 */
export function DifficultyBar({
  difficulty,
  compact = false,
  words = false,
  size = 11,
  className = "",
}: {
  difficulty: string | null | undefined;
  compact?: boolean;
  words?: boolean;
  size?: number;
  className?: string;
}) {
  const grade = difficultyGrade(difficulty);
  if (!grade) {
    return null;
  }
  const label = difficultyLabel(difficulty);
  const scale = <DifficultyScale grade={grade} size={size} />;
  if (compact) {
    return (
      // the two words a touch smaller than the steps they stand beside
      <span className={`inline-flex items-center gap-2 whitespace-nowrap text-ink-soft ${className}`} style={{ fontSize: words ? size - 1 : size + 1 }} title={label} aria-label={`Dificuldade: ${label}`}>
        {words && <span>Fácil</span>}
        {scale}
        {words && <span>Difícil</span>}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={`inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] text-ink-soft ${className}`}
      title={label}
      aria-label={`Dificuldade: ${label}`}
      onClick={(e) => {
        e.stopPropagation();
        openFaq("dificuldade");
      }}
    >
      <small className="underline underline-offset-2">Fácil</small>
      {scale}
      <small className="underline underline-offset-2">Difícil</small>
    </button>
  );
}

/*
 * One FAQ, opened from beside what it answers: the question and its answer, in
 * a sheet. The FAQ is found by the tag given it in the backoffice
 * (GET /public-api/faq/:tag), so the app names the tag and staff choose which
 * answer carries it. Nothing when no FAQ has the tag.
 */
type Faq = { id: number; title: string; content: string };

let faqOpener: ((faq: Faq) => void) | null = null;

export const openFaq = async (tag: string) => {
  const faq = await api.get<Faq>(`/public-api/faq/${encodeURIComponent(tag)}`, { silent: true });
  if (!faq || !faq.title) {
    return;
  }
  track("view_faq", { faq: tag });
  faqOpener?.(faq);
};

/** mounted once by the event's page, so openFaq has somewhere to open */
export function FaqHost() {
  const [faq, setFaq] = useState<Faq | null>(null);

  useEffect(() => {
    faqOpener = (opened) => setFaq(opened);
    return () => {
      faqOpener = null;
    };
  }, []);

  return (
    <Sheet open={!!faq} onClose={() => setFaq(null)} title={faq?.title} width={520}>
      <div className="rich-text px-4 pb-5 text-[14.5px]" dangerouslySetInnerHTML={{ __html: faq?.content || "" }} />
    </Sheet>
  );
}
